import { useEffect, useRef } from 'react';

import { useSetRecoilState } from 'recoil';

import useMemoDeep from '@cc/shared/hooks/useMemoDeep';
import getRandomId from '@cc/shared/utils/getRandomId';

import {
  useAnswers,
  useCalculator,
  useCalculatorId,
  useIsEditing,
  useIsLiveMode,
  useIsProduction,
  useIsTesting,
  useOutputs,
  useProfile,
  useSelectedView,
} from '../CalculatorState';
import { useDevToolsContext } from '../DevTools/DevTools';
import CalculatorStyle from '../components/CalculatorStyle';
import Outline from '../components/Outline';
import TreeRenderer from '../components/TreeRenderer';
import View from '../components/View';
import ViewNavigation from '../components/ViewNavigation';
import isValidState from '../recoil/isValidState';
import useSetFormulaContext from '../recoil/useSetFormulaContext';
import viewCountState from '../recoil/viewCountState';
import getTreeNew from '../util/getTreeNew';
import triggerEvent from '../util/triggerEvent';
import useWebFontLoader from '../util/useWebFontLoader';
import validate from '../util/validate';

import useEventTriggerer from './useEventTriggerer';
import useResizeDetector from './useResizeDetector';
import getIsFramed from '../util/getIsFramed';

type CanvasProps = {
  embedType?: 'inPage' | 'framed' | 'standalone';
};

const CanvasNew = ({ embedType = 'inPage' }: CanvasProps) => {
  const calculator = useCalculator();
  const isEditing = useIsEditing();
  const isLiveMode = useIsLiveMode();
  const isTesting = useIsTesting();
  const isProduction = useIsProduction();

  const profile = useProfile();

  // TODO: Move this to recoil state or Context
  const settings = useMemoDeep(() => {
    const {
      numberFormatting,
      language,
      messages,
      shouldShowViewBreakNavigation,
      systemOfMeasurement,
      snipcartApiKey,
      stripeUserId,
    } = calculator;

    return {
      numberFormatting,
      language,
      messages,
      shouldShowViewBreakNavigation,
      systemOfMeasurement,
      snipcartApiKey,
      stripeUserId,
      isEditing,
      isTesting,
      isProduction,
    };
  }, [calculator]);

  const tree = getTreeNew(calculator);

  const setViewCount = useSetRecoilState(viewCountState);

  useEffect(() => {
    setViewCount(tree.length);
  }, [tree.length]);

  // TODO: Refactor to CalculatorState
  const answers = useAnswers();
  const selectedView = useSelectedView();

  const outputs = useOutputs();
  const isValid = validate({
    calculator,
    outputs,
    answers,
    items: tree[selectedView],
  });

  const setIsValid = useSetRecoilState(isValidState);

  useEffect(() => {
    setIsValid(isValid);
  }, [isValid]);

  // Jump to top on selected view change
  const calculatorId = useCalculatorId();
  const shouldScrollToTopRef = useRef(false);

  useEffect(() => {
    if (!shouldScrollToTopRef.current) {
      shouldScrollToTopRef.current = true;

      return;
    }

    triggerEvent({
      calculatorId,
      type: 'scrollToTop',
    });
  }, [selectedView]);

  const calculatorRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  useResizeDetector({ calculatorEl: calculatorRef.current, calculatorId });

  useEventTriggerer({
    calculatorContainer: canvasRef.current,
    embedType,
  });

  useWebFontLoader();

  const { isDevToolsOpen } = useDevToolsContext();

  const setFormulaContext = useSetFormulaContext();

  useEffect(() => {
    // We get or set a session id for the visitor.
    const sessionIdKey = 'visitorSessionId';
    let sessionId = window.sessionStorage.getItem(sessionIdKey);
    if (!sessionId) {
      const newSessionId = getRandomId();
      window.sessionStorage.setItem(sessionIdKey, newSessionId);
      sessionId = newSessionId;
    }
    setFormulaContext({ SESSION_ID: { value: sessionId, type: 'context' } });
  }, [setFormulaContext]);

  useEffect(() => {
    if (!getIsFramed() || window.parent == null) {
      return;
    }

    const listener = (ev: MessageEvent) => {
      const { data = {} } = ev;
      const { type, payload, calculatorId: eventCalculatorId } = data;

      if (eventCalculatorId !== calculatorId) return;

      if (type === 'embedParams') {
        setFormulaContext({
          embedParams: { value: payload, type: 'internal' },
        });
      }
    };

    window.addEventListener('message', listener);

    // We tell the iframe embed script the calc is ready i.e. has an message listener attached.
    window.parent.postMessage({ type: 'ready', calculatorId }, '*');

    return () => {
      window.removeEventListener('message', listener);
    };
  }, [setFormulaContext]);

  return (
    <div
      ref={calculatorRef}
      className="cc-calculator"
      style={{
        height: '100%',
        marginBottom: isDevToolsOpen ? 400 : 0,
      }}
    >
      <CalculatorStyle
        canvasRef={canvasRef}
        className="cc"
        embedType={embedType}
      >
        <Views settings={settings} tree={tree}></Views>

        {tree.length > 1 &&
          (!isEditing || isLiveMode) &&
          settings.shouldShowViewBreakNavigation && (
            <ViewNavigation calculator={calculator} tree={tree} />
          )}

        <Outline
          calculator={calculator}
          embedType={embedType}
          profile={profile}
          messages={settings.messages}
        />
      </CalculatorStyle>
    </div>
  );
};

/**
 * This is a temporary component that renders the views generated from view breaks.
 * Once View Breaks is migrated to View Container, we can get rid of this.
 */
const Views = ({ settings, tree }) => {
  const isEditing = useIsEditing();
  const isLiveMode = useIsLiveMode();
  const selectedView = useSelectedView();

  return (
    <>
      {tree.map((items, viewIndex) => {
        return (
          <View
            // eslint-disable-next-line react/no-array-index-key
            key={`view-${viewIndex}`}
            isActive={selectedView === viewIndex}
            isLast={tree.length === viewIndex + 1}
            isEditing={isEditing}
            isLiveMode={isLiveMode}
            hasChildren={items.length}
          >
            <TreeRenderer
              items={items}
              treePath={[viewIndex]}
              settings={settings}
            />
          </View>
        );
      })}
    </>
  );
};

export default CanvasNew;
